import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 26000-26999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const takeoffErrors = {
  // 26xxx is the takeoffs module's series. Seven of its sites needed no new code — four reused
  // ESTIMATE_NOT_FOUND (1101), two ESTIMATE_VERSION_NOT_FOUND (1117), one
  // MEASUREMENT_UNIT_NOT_FOUND (1125), and one FILE_VERSION_NOT_FOUND (25001) — all verbatim.
  // Three stay generic, literally saying "Something went wrong".
  // 26001-26012 — the takeoffs module's split.
  TAKEOFF_MEASUREMENT_UNIT_NO_SYMBOL_FOR_PORTAL: {
    errorCode: 26001,
    errorMessage: t("Measurement unit has no symbol for this portal"),
    isDynamic: false,
  },
  TAKEOFF_AUTOMATION_QUERY_FILE_VERSION_NOT_FOUND: {
    errorCode: 26002,
    errorMessage: t("Automation query file version not found"),
    isDynamic: false,
  },
  TAKEOFF_NOT_IN_ESTIMATE_OR_NO_QUERY: {
    errorCode: 26003,
    errorMessage: t("This Takeoff doesn't exists in the Estimate or it doesn't attached to any Query!"),
    isDynamic: false,
  },
  TAKEOFF_ALREADY_ON_LATEST_QUERY: {
    errorCode: 26004,
    errorMessage: t("This Takeoff already associated to latest Query!"),
    isDynamic: false,
  },
  TAKEOFF_AUTOMATION_QUERY_NO_FILE_VERSION: {
    errorCode: 26005,
    errorMessage: t("Takeoff automation query has no file version"),
    isDynamic: false,
  },
  TAKEOFF_ATTACHED_QUERY_NOT_FOUND: {
    errorCode: 26006,
    errorMessage: t("Attached query no longer exists"),
    isDynamic: false,
  },
  TAKEOFF_ESTIMATE_ID_REQUIRED: {
    errorCode: 26007,
    errorMessage: t("estimateId is required"),
    isDynamic: false,
  },
  // Deliberately not TAKEOFF_NOT_FOUND (1111): that code's sentence names the estimate's current
  // version specifically, this one does not.
  TAKEOFF_NOT_FOUND_PLAIN: {
    errorCode: 26008,
    errorMessage: t("Takeoff not found!"),
    isDynamic: false,
  },
  // Replaces `estimateErrorMessages.estimateTakeoffWizardCostDatabaseNotFound`. Deliberately not
  // ESTIMATE_COST_DATABASE_UPGRADE_TAKEOFF_WIZARD_ERROR (1201) despite the name: 1201's own single
  // call site ships "Something went wrong" today, so reusing it would change THAT site's text.
  TAKEOFF_WIZARD_COST_DATABASE_NOT_LINKED: {
    errorCode: 26009,
    errorMessage: t(
      "The Model to Takeoff Wizard requires the Estimate to be linked to a Cost Database, which is currently missing.",
    ),
    isDynamic: false,
  },
  TAKEOFF_WIZARD_CONFIGURATION_REQUIRED: {
    errorCode: 26010,
    errorMessage: t("Wizard configuration (quantityParameter, unitField, codeField) is required"),
    isDynamic: false,
  },
  TAKEOFF_WIZARD_NO_RECORDS_TO_ADD: {
    errorCode: 26011,
    errorMessage: t("No records found to add"),
    isDynamic: false,
  },
  TAKEOFF_WIZARD_ID_REQUIRED: {
    errorCode: 26012,
    errorMessage: t("wizardId is required"),
    isDynamic: false,
  },
  TAKEOFF_ITEMS_ALREADY_ADDED: {
    errorCode: 26013,
    errorMessage: t(
      "None of the selected items could be added to the takeoff — they are already linked to it, or their measurement unit type does not match it.",
    ),
    isDynamic: false,
  },
  // 26014 arrived from staging's automation-query lookup. Deliberately not
  // TAKEOFF_ATTACHED_QUERY_NOT_FOUND (26006), whose sentence ("Attached query no longer exists")
  // describes a query that WAS attached and has since gone; this one is a lookup that never resolved.
  TAKEOFF_AUTOMATION_QUERY_NOT_FOUND: {
    errorCode: 26014,
    errorMessage: t("Automation query not found"),
    isDynamic: false,
  },
  // Attached items share the takeoff's unit, so it changes only once they are removed (the web removes them first).
  TAKEOFF_UNIT_NOT_EDITABLE_WITH_ITEMS: {
    errorCode: 26015,
    errorMessage: t(
      "The unit of this takeoff can't be changed while items are attached to it. Remove the items first.",
    ),
    isDynamic: false,
  },
  // 26016: a takeoff whose stored `automationQuery.quantity` is not a model property path. The path becomes a
  // files_properties $project key and field path, so the run is refused rather than measured from it.
  TAKEOFF_AUTOMATION_QUANTITY_INVALID: {
    errorCode: 26016,
    errorMessage: t(
      "A takeoff's quantity parameter is not a model property. Remove the takeoff's query and attach it again, choosing the quantity parameter from the list.",
    ),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
